import React, {
  useEffect,
  useContext,
  useState,
  useCallback,
  useRef
} from 'react'
import { useRouter } from 'next/router'
import {
  Box,
  Flex,
  Text,
  Button,
  Image,
  Shimmer,
  theme
} from 'pcln-design-system'
import { isEmpty, join, keys } from 'ramda'
import styled from 'styled-components'
import { themeGet } from '@styled-system/theme-get'
import pclnExperimentation from '@pcln/experimentation'
import { Calendar } from '@pcln/date-picker'
import { useForm } from 'react-hook-form'
import { format, addDays } from 'date-fns'
import Sticky from 'react-stickynode'
import { Pin } from 'pcln-icons'
import { getHotelDateRange } from '@/shared-utils/date-helper'
import SearchFormButton from '@/components/SearchFormButton'
import TypeAhead from '@/components/TypeAhead'
import { PopoverContainer } from '@/components/Popover'
import ShadowEffect from '@/components/ShadowEffect'
import CalendarWrapperBox from '@/components/CalenderWrapperBox.styled'
import DateField from '@/components/DateField'
import TravelerSelection from '@/components/TravelerSelection/TravelerSelection'
import { VP_DISCLAIMER } from '@/components/TravelerSelection/constants'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import useTabGroup from '@/hooks/useTabGroup'
import StickyEffect from '@/components/StickyEffect'
import BundleAndSave from '@/components/BundleAndSave/BundleAndSave'
import analytics from '@/shared-utils/analytics'
import isMoreThanFourPerRoom from '@/shared-utils/packages-room-count'
import {
  ANALYTICS_ACTION_HOTELS_SEARCH,
  ANALYTICS_CATEGORIES,
  INVALID
} from '@/constants/analytics'
import { LOCATION_SEARCH_TYPE } from '@/types'
import useCityNameInBtnTextExperiment from '@/hooks/useCityNameInBtnTextExperiment'
import useFetchCityMap from '@/hooks/useFetchCityMap'
import usePrePopPrepaidSearch from '@/hooks/usePrePopPrepaidSearch'
import ExperimentContext from '@/context/ExperimentContext'
import useSeti from '@/hooks/useSeti'
import useGA4AnalyticsForSETI from '@/hooks/useGA4Analytics'
import {
  SearchFormContext,
  SearchFormContextType
} from '@/context/SearchFormContext'
import {
  ADD_PRODUCT_OPTIONS,
  CALENDAR_POPOVER_SELECTOR,
  PromoDeal,
  FLY,
  DRIVE,
  STAY
} from '../constants'
import { fields, initialState, groupDealsURL } from './utils'
import {
  searchFormSchema,
  isInfantLimitReachedPackages,
  INFANT_CAPACITY_LIMIT
} from './validation'
import {
  PromoDealType,
  HOTEL_FORM_STATE_TYPE,
  HOTEL_FORM_SUBMIT_TYPE,
  SEM_SEARCH_TYPE
} from '../types'

const StyledRelative = styled(Box)`
  position: relative;
  overflow: hidden;
`

const StyledAbsolute = styled(Box)`
  position: absolute;
  top: ${themeGet('space.4')}px;
  right: ${themeGet('space.3')}px;
`

type FormType = {
  onSubmit: (data: HOTEL_FORM_SUBMIT_TYPE) => void
  promoDeal?: PromoDealType
  setLocation?: (city?: LOCATION_SEARCH_TYPE) => void
  setHasDateChanged?: (isDateChanged: boolean) => void
}

export default function Form({
  onSubmit,
  promoDeal,
  setHasDateChanged,
  setLocation = () => {
    // if pre-pop isn't used
  }
}: FormType) {
  const [isHotelCalendarOpen, setIsHotelCalendarOpen] = useState(false)
  const { registerTabElement, focusNextElement } = useTabGroup()
  const {
    isMobile = false,
    prePopData,
    webstats,
    customer
  } = useContext(BootstrapDataContext)
  const { updateFormValues } = useContext(
    SearchFormContext as React.Context<
      SearchFormContextType<HOTEL_FORM_STATE_TYPE>
    >
  )

  const prePaidPrePop = usePrePopPrepaidSearch()

  const {
    endLocation,
    startDate: prePopStartDate,
    endDate: prePopEndDate,
    roomCount
  } = prePopData?.hotels ?? prePaidPrePop ?? {}
  const [isStickyInEffect, setIsStickyInEffect] = useState(false)
  const enableStickyEffect = !isMobile && !promoDeal
  const { query, pathname } = useRouter()
  const isHomepage = pathname === '/'
  const ANALYTICS_CATEGORY = ANALYTICS_CATEGORIES[pathname]
  const ORIGINAL_MOBILE_CITY_MAP_HEIGHT = 130
  const MOBILE_CITY_MAP_HEIGHT_CONTAINER = 80

  const { SEMSearch, SEMSEARCH, 'ambig-hotel-city': ambigHotelCity } = query
  const initialValues = {
    ...initialState,
    ...(endLocation && { endLocation: endLocation as LOCATION_SEARCH_TYPE }),
    ...(prePopStartDate && { startDate: prePopStartDate }),
    ...(prePopEndDate && { endDate: prePopEndDate }),
    ...(roomCount && { roomCount })
  }

  const isMoreRoomCapacityAllowed =
    useSeti('PKG_FE_SEARCH_FORM_HOTEL_NUM_ADULTS_RESTRICTION', false) ===
    'VARIANT'

  const isMobileFormInputSmaller = isMobile && !isHomepage
  // eslint-disable can be removed after upgrade to react-hook-form v7 - https://github.com/react-hook-form/react-hook-form/issues/2887
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const {
    clearError,
    register,
    unregister,
    setValue,
    getValues,
    handleSubmit,
    watch,
    triggerValidation,
    errors
  } = useForm<HOTEL_FORM_STATE_TYPE>({
    defaultValues: initialValues,
    mode: 'onSubmit',
    validationSchema: searchFormSchema,
    reValidateMode: 'onChange',
    validationContext: {
      isMoreRoomCapacityAllowed
    }
  })

  const [cityMapWidth, setCityMapWidth] = useState<number | undefined>(
    undefined
  )
  useEffect(() => {
    setCityMapWidth(window.screen.width)
  }, [])

  useEffect(() => {
    fields.forEach(name => {
      register({ name })
    })
    return () => {
      fields.forEach(name => {
        unregister(name)
      })
    }
  }, [register, unregister])

  const {
    endLocation: watchEndLocation,
    startLocation: watchStartLocation,
    startDate: watchStartDate,
    endDate: watchEndDate,
    roomCount: watchRoomCount,
    adultCount: watchAdultCount,
    childrenAges: watchChildrenAges,
    childrenCount: watchChildrenCount,
    tripType: watchTripType
  } = watch()

  useEffect(() => {
    if (watchEndLocation) {
      setLocation(watchEndLocation)
    }
  }, [watchEndLocation, setLocation])

  // PKG_FE_SEARCH_FORM_HOTEL_NUM_ADULTS_RESTRICTION
  useEffect(() => {
    if (
      watchTripType.includes(FLY) &&
      isMoreThanFourPerRoom(
        watchRoomCount,
        watchAdultCount + watchChildrenCount
      )
    ) {
      pclnExperimentation.fireImpression(
        'PKG_FE_SEARCH_FORM_HOTEL_NUM_ADULTS_RESTRICTION'
      )
    }
  }, [watchTripType, watchRoomCount, watchAdultCount, watchChildrenCount])

  const { logGA4Event } = useGA4AnalyticsForSETI(
    'MKTG_MWEB_HTL_PROD_HP_APP_MODAL'
  )

  useEffect(() => {
    if (
      isMobile &&
      !isHomepage &&
      window?.PCLN?.pclnEventDispatcher?.dispatch
    ) {
      pclnExperimentation.fireImpression('MKTG_MWEB_HTL_PROD_HP_APP_MODAL')
      logGA4Event()
    }
  }, [logGA4Event, isMobile, isHomepage])

  const { MKTG_MWEB_HTL_PROD_HP_APP_MODAL } = useContext(ExperimentContext)

  const searchBtnText = useCityNameInBtnTextExperiment(
    watchTripType,
    watchEndLocation
  )
  const hasFlight = watchTripType.includes(FLY)
  const hasCar = watchTripType.includes(DRIVE)
  const isHPWithoutStickyEffect = isHomepage && !isStickyInEffect

  const HTL_MAX_DAYS_ALLOWED = useSeti('HTL_MAX_DAYS_ALLOWED', false)
  const maxDaysToSelect = HTL_MAX_DAYS_ALLOWED === 'VARIANT' ? 32 : 30

  const { lat, lon, cityName } =
    (prePopData?.hotels?.endLocation as LOCATION_SEARCH_TYPE) ?? {}

  const showCityMapInMobile =
    isMobile && !hasFlight && !hasCar && !isHomepage && !!cityName

  const showCityDisambiguation = !!ambigHotelCity && !!cityName && !isHomepage

  const { cityMapURL, fetchCityMap } = useFetchCityMap(
    showCityMapInMobile
      ? {
          lat,
          lon,
          width: cityMapWidth,
          height: ORIGINAL_MOBILE_CITY_MAP_HEIGHT
        }
      : {
          lat: undefined,
          lon: undefined,
          width: undefined,
          height: undefined
        }
  )

  const inputFormMargin = !isMobile || isMobileFormInputSmaller ? 3 : 0

  useEffect(() => {
    if (!prePopData && prePaidPrePop) {
      setValue('startDate', prePaidPrePop?.startDate)
      setValue('endDate', prePaidPrePop?.endDate)
      setValue('endLocation', prePaidPrePop?.endLocation)
    }
  }, [prePaidPrePop, prePopData, setValue])

  useEffect(() => {
    const shouldPopulateDates = !watchStartDate && !watchEndDate
    const start = shouldPopulateDates
      ? format(new Date(), 'yyyyMMdd')
      : watchStartDate
    const end = shouldPopulateDates
      ? format(addDays(new Date(), 1), 'yyyyMMdd')
      : watchEndDate
    if (shouldPopulateDates) {
      setValue('startDate', start)
      setValue('endDate', end)
    }
  }, [setValue, watchStartDate, watchEndDate])

  const isOneLineSearch =
    !isMobile &&
    !promoDeal &&
    (isStickyInEffect || !isHomepage) &&
    (isStickyInEffect || !hasFlight)

  const smbtBtnReplacementGrid = isHPWithoutStickyEffect ? 1 / 2 : 1 / 3
  const smbtBtnReplacementGridRow2 = isHPWithoutStickyEffect ? 1 / 2 : 2 / 5
  const smbtBtnReplacementGridRow3 = isHPWithoutStickyEffect ? 1 / 2 : 1 / 5
  const smbtBtnReplacementGridTypeAhead = isHPWithoutStickyEffect ? 1 : 1 / 2

  const marginBottomButtonWithoutStickySearch = 0
  const marginBottomButtonStickySearch = [4, null, 3]
  const marginButtonBasedOnStickySearch = isStickyInEffect
    ? marginBottomButtonStickySearch
    : marginBottomButtonWithoutStickySearch
  const searchBtnMargin =
    !isMobile && isHPWithoutStickyEffect ? 1 : marginButtonBasedOnStickySearch

  // eslint-disable-next-line no-nested-ternary
  const typeaheadWidth = isOneLineSearch
    ? hasFlight
      ? [1, null, 24 / 100]
      : [1, null, 27 / 100]
    : hasFlight
    ? [1, null, 1 / 2, null, 1 / 2]
    : [1, null, smbtBtnReplacementGridTypeAhead, null, 1]

  // eslint-disable-next-line no-nested-ternary
  const calendarWidth = isOneLineSearch
    ? hasFlight
      ? [1, null, 24 / 100]
      : [1, null, 31 / 100]
    : hasFlight
    ? [1, null, smbtBtnReplacementGridRow2, null, smbtBtnReplacementGrid]
    : [1, null, 1 / 2, null, smbtBtnReplacementGrid]

  // eslint-disable-next-line no-nested-ternary
  const roomSelectionWidth = isOneLineSearch
    ? hasFlight
      ? [1, null, 18 / 100]
      : [1, null, 23 / 100]
    : hasFlight
    ? [1, null, smbtBtnReplacementGridRow2, null, smbtBtnReplacementGrid]
    : [1, null, 1 / 2, null, smbtBtnReplacementGrid]

  // eslint-disable-next-line no-nested-ternary
  const searchButtonWidth = isOneLineSearch
    ? hasFlight
      ? [1, null, 10 / 100]
      : [1, null, 19 / 100]
    : hasFlight
    ? [1, null, smbtBtnReplacementGridRow3, null, smbtBtnReplacementGrid]
    : [1, null, 1 / 2, null, smbtBtnReplacementGrid]

  // eslint-disable-next-line no-nested-ternary
  const packageOptionsWidth = isOneLineSearch
    ? hasFlight
      ? [1, null, 10 / 100]
      : [1, null, 19 / 100]
    : [1, null, 1 / 2, null, 1 / 2]

  const hotelPlannerLink = groupDealsURL({
    location: watchEndLocation ? watchEndLocation.itemName : '',
    startDate: watchStartDate,
    endDate: watchEndDate
  })

  const hotelPlannerLinkAttrsProp = {
    href: hotelPlannerLink,
    target: '_blank'
  }

  const handleShowBundleAndSaveWidth =
    // eslint-disable-next-line no-nested-ternary
    isHPWithoutStickyEffect && !isMobile
      ? packageOptionsWidth
      : isMobile
      ? '100%'
      : ''
  const handleStickyEffectStateChange = ({ status }: Sticky.Status) => {
    if (status === Sticky.STATUS_FIXED && !isStickyInEffect) {
      setIsStickyInEffect(true)
    } else if (isStickyInEffect) {
      setIsStickyInEffect(false)
    }
  }
  const showBundleAndSave = () =>
    !promoDeal && (
      <Box
        width={handleShowBundleAndSaveWidth}
        px={isHPWithoutStickyEffect && !isMobile && 2}
        py={[2, null, 0]}
        mb={isMobile || !isHPWithoutStickyEffect ? 3 : 4}
        height={56}
      >
        <BundleAndSave
          category={ANALYTICS_CATEGORY}
          productOptions={ADD_PRODUCT_OPTIONS}
          tripType={watchTripType}
          label="Bundle + Save"
          setValue={setValue}
        />
      </Box>
    )
  const showCurrentLocation = !promoDeal && isMobile

  useEffect(() => {
    clearError('startLocation')
  }, [clearError, watchStartLocation])
  useEffect(() => {
    clearError('endLocation')
  }, [clearError, watchEndLocation])
  useEffect(() => {
    clearError('startDate')
  }, [clearError, watchStartDate])
  useEffect(() => {
    clearError('endDate')
  }, [clearError, watchEndDate])
  useEffect(() => {
    triggerValidation('childrenCount')
  }, [
    triggerValidation,
    watchStartLocation,
    watchEndLocation,
    watchChildrenAges,
    watchAdultCount,
    watchChildrenCount
  ])

  useEffect(() => {
    triggerValidation('roomCount')
  }, [
    triggerValidation,
    watchChildrenCount,
    watchRoomCount,
    watchAdultCount,
    watchTripType
  ])

  useEffect(() => {
    if (updateFormValues) {
      updateFormValues(getValues())
    }
  }, [
    getValues,
    updateFormValues,
    watchStartLocation,
    watchEndLocation,
    watchStartDate,
    watchEndDate,
    watchChildrenAges,
    watchAdultCount,
    watchChildrenCount,
    watchRoomCount,
    watchTripType
  ])

  useEffect(() => {
    if (!isEmpty(errors)) {
      const label = `${INVALID} - ${join(' | ', keys(errors))}`
      analytics.fireEvent({
        action: isStickyInEffect
          ? 'Hotel Sticky Search Button'
          : ANALYTICS_ACTION_HOTELS_SEARCH,
        category: ANALYTICS_CATEGORY,
        label
      })
    }
  }, [errors, ANALYTICS_CATEGORY, isStickyInEffect])

  const itemSelected = useCallback(
    (item: LOCATION_SEARCH_TYPE) => {
      const gaTracking = () => {
        analytics.fireEvent({
          action: 'Field click',
          category: ANALYTICS_CATEGORY,
          label: 'Typeahead'
        })
      }
      if (item) {
        gaTracking()
      }
      if (setHasDateChanged) setHasDateChanged(true)
      if (showCityMapInMobile && item) {
        fetchCityMap({
          lat: item?.lat,
          lon: item?.lon,
          width: window.screen.width,
          height: ORIGINAL_MOBILE_CITY_MAP_HEIGHT
        })
      }

      if (!isMobile && !!item) {
        focusNextElement()
      } else if (isMobile && !!item && document.activeElement) {
        ;(document.activeElement as HTMLElement).blur()
      }
    },
    [
      setHasDateChanged,
      showCityMapInMobile,
      isMobile,
      ANALYTICS_CATEGORY,
      fetchCityMap,
      focusNextElement
    ]
  )

  const ambiguousElement = useRef<HTMLInputElement>()

  function registerAmbiguousElement(ref: HTMLInputElement): void {
    ambiguousElement.current = ref
  }
  const clarifyAmbiguity = useCallback(() => {
    const city = endLocation?.cityName ?? ''
    return setTimeout(() => {
      if (ambiguousElement.current && city) {
        ambiguousElement.current.focus()
        const ambiguousCity: any = { ...endLocation, itemName: city }
        setValue('endLocation', ambiguousCity)
      }
    }, 0)
  }, [endLocation, setValue])

  const showVPDisclaimer =
    watchTripType.includes(FLY) || watchTripType.includes(DRIVE)
  const HP_LP_BUTTON_MICROANIMATION = useSeti(
    'HOTELS_LP_BUTTON_MICROANIMATION',
    !(isHomepage && !isMobile)
  )
  const htlButtonMicroAnimation =
    (isHomepage && !isMobile) || HP_LP_BUTTON_MICROANIMATION === 'VARIANT'
  const htlButtonMicroAnimationProps = htlButtonMicroAnimation
    ? {
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        scale: theme.scale['scale-100'] + theme.scale['scale-2'],
        animatedBoxShadow: theme.shadows.xl
      }
    : null

  return (
    <form
      data-testid={promoDeal ? 'express-deal-form' : 'form'}
      aria-label={
        promoDeal ? 'hotel-express-deal-search-form' : 'hotel-search-form'
      }
      onSubmit={handleSubmit((data: HOTEL_FORM_STATE_TYPE) =>
        onSubmit({
          ...data,
          gaCategory: ANALYTICS_CATEGORY,
          ...(SEMSearch && { SEMSearch: SEMSearch as SEM_SEARCH_TYPE }),
          ...(SEMSEARCH && { SEMSEARCH: SEMSEARCH as SEM_SEARCH_TYPE }),
          gaAction: isStickyInEffect
            ? 'Hotel Sticky Search Button'
            : ANALYTICS_ACTION_HOTELS_SEARCH,
          mapView: false
        })
      )}
    >
      <>
        <StickyEffect
          enabled={enableStickyEffect}
          isStickyInEffect={isStickyInEffect}
          handleStickyEffectStateChange={handleStickyEffectStateChange}
          stickyHeight={120}
        >
          <Flex
            key={0}
            mx={-2}
            flexWrap={['wrap', isOneLineSearch ? null : 'wrap']}
          >
            {hasFlight && (
              <Box
                width={typeaheadWidth}
                mb={inputFormMargin}
                pl={2}
                pr={isOneLineSearch ? 1 : 2}
                data-testid="DASH_TAB_HOTELS_STARTLOCATION"
              >
                <ShadowEffect forProperty="location">
                  {({
                    disableShadowState
                  }: {
                    disableShadowState: () => void
                  }) => (
                    <Box textAlign="left">
                      <TypeAhead
                        searchProduct="packagesOrigin"
                        label="Where from?"
                        placeholder="Where from?"
                        searchKey="startLocation"
                        setValue={setValue}
                        errors={errors}
                        disableShadowState={disableShadowState}
                        defaultSelectedItem={watchStartLocation}
                        ref={registerTabElement}
                        showCurrentLocation={showCurrentLocation}
                        onItemSelect={itemSelected}
                      />
                    </Box>
                  )}
                </ShadowEffect>
              </Box>
            )}
            <Box
              width={typeaheadWidth}
              mb={showCityDisambiguation ? 1 : inputFormMargin}
              pl={hasFlight && isOneLineSearch ? 1 : 2}
              pr={isOneLineSearch ? 1 : 2}
              data-testid="DASH_TAB_HOTELS_ENDLOCATION"
            >
              <ShadowEffect forProperty="location">
                {({
                  disableShadowState
                }: {
                  disableShadowState: () => void
                }) => (
                  <Box textAlign="left">
                    <TypeAhead
                      searchProduct={
                        hasFlight ? 'packagesDestination' : 'hotels'
                      }
                      label="Where to?"
                      placeholder="Where to?"
                      searchKey="endLocation"
                      setValue={setValue}
                      errors={errors}
                      disableShadowState={disableShadowState}
                      defaultSelectedItem={watchEndLocation}
                      ref={(elem: HTMLInputElement) => {
                        registerTabElement(elem)
                        if (showCityDisambiguation) {
                          registerAmbiguousElement(elem)
                        }
                      }}
                      showCurrentLocation={showCurrentLocation}
                      onItemSelect={itemSelected}
                    />
                  </Box>
                )}
              </ShadowEffect>
              {showCityDisambiguation && (
                <Box py={[2, null, 1]}>
                  <Button
                    variation="link"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault()
                      clarifyAmbiguity()
                      analytics.fireEvent({
                        action: 'ambiguous-city',
                        category: ANALYTICS_CATEGORY,
                        label: cityName
                      })
                    }}
                  >
                    <Text fontSize={0}>
                      Looking for a different {cityName}?
                    </Text>
                  </Button>
                </Box>
              )}
            </Box>
            <Box
              width={calendarWidth}
              px={isOneLineSearch ? 1 : 2}
              data-testid="DASH_TAB_HOTELS_DATES"
              id="hotel-date-range-box"
              textAlign="left"
            >
              <ShadowEffect forProperty="hotel-date-range">
                {({
                  disableShadowState
                }: {
                  disableShadowState: () => void
                }) => (
                  <PopoverContainer
                    open={isHotelCalendarOpen}
                    data-calendar
                    onDismiss={() => {
                      setIsHotelCalendarOpen(false)
                      disableShadowState()
                    }}
                    domSelectorForVisuallyContainedElement={
                      CALENDAR_POPOVER_SELECTOR
                    }
                  >
                    <DateField
                      id="hotel-date-range"
                      data-testid="hotel-date-range"
                      m={0}
                      ref={registerTabElement}
                      aria-expanded={isHotelCalendarOpen ? 'true' : 'false'}
                      error={(() => {
                        if (errors.startDate) {
                          return errors.startDate.message
                        }
                        if (errors.endDate) {
                          return errors.endDate.message
                        }
                      })()}
                      label="Check-in - Check-out"
                      name="hotelDates"
                      onFocus={() => {
                        pclnExperimentation.fireImpression(
                          'HTL_MAX_DAYS_ALLOWED'
                        )
                        setIsHotelCalendarOpen(true)
                      }}
                      value={getHotelDateRange({
                        startDate: watchStartDate,
                        endDate: watchEndDate
                      })}
                    />
                    <CalendarWrapperBox>
                      <Calendar
                        roundedCorners
                        startDate={watchStartDate}
                        endDate={watchEndDate}
                        maxDaysToSelect={maxDaysToSelect}
                        monthWidthPx={350}
                        onChange={(
                          _: unknown,
                          {
                            startDate,
                            endDate
                          }: { startDate: string; endDate: string }
                        ) => {
                          if (startDate && endDate) {
                            setIsHotelCalendarOpen(false)
                            disableShadowState()
                          }
                          if (setHasDateChanged) setHasDateChanged(true)
                          setValue('startDate', startDate || '')
                          setValue('endDate', endDate || '')
                        }}
                        monthsToShow={2}
                        onDismiss={() => {
                          setIsHotelCalendarOpen(false)
                          disableShadowState()
                        }}
                        useDefaultFooter
                      />
                    </CalendarWrapperBox>
                  </PopoverContainer>
                )}
              </ShadowEffect>
            </Box>
            <Box
              width={roomSelectionWidth}
              mb={3}
              px={isOneLineSearch ? 1 : 2}
              data-testid="DASH_TAB_HOTELS_NUMROOMS"
            >
              <ShadowEffect
                enabled
                forProperty="travelers-selection-readonly-input"
                alternativeBlurHandling
              >
                {({
                  disableShadowState
                }: {
                  disableShadowState: () => void
                }) => (
                  <TravelerSelection
                    adultCount={watchAdultCount}
                    childrenAges={watchChildrenAges}
                    childrenCount={watchChildrenCount}
                    disableShadowState={disableShadowState}
                    hotelPlannerLinkAttrs={hotelPlannerLinkAttrsProp}
                    disclaimer={showVPDisclaimer ? VP_DISCLAIMER : ''}
                    isMobile={isMobile}
                    error={
                      errors?.childrenCount?.message ||
                      errors?.roomCount?.message ||
                      (isInfantLimitReachedPackages(
                        watchAdultCount,
                        watchChildrenAges,
                        watchTripType
                      ) &&
                        INFANT_CAPACITY_LIMIT) ||
                      ''
                    }
                    infantCount={0}
                    maxAdults={watchTripType === STAY ? 28 : undefined}
                    maxChildren={watchTripType === STAY ? 8 : undefined}
                    maxRooms={watchTripType === STAY ? 9 : undefined}
                    onDoneButtonClick={disableShadowState}
                    roomCount={watchRoomCount}
                    setValue={setValue}
                    showChildAgeSelectors
                    showInfantMenuItem={false}
                    showRoomMenuItem
                    showSubLabel={false}
                    showYouthMenuItem={false}
                    youthCount={0}
                  />
                )}
              </ShadowEffect>
            </Box>
            {(isMobile || isHPWithoutStickyEffect) && showBundleAndSave()}
            <Box
              width={searchButtonWidth}
              pl={isOneLineSearch ? 1 : 2}
              pr={2}
              mb={showCityMapInMobile ? 0 : searchBtnMargin}
              ml="center"
              style={{
                'overflow-y': !isMobile && 'visible',
                height: !isMobile && (isStickyInEffect || !isHomepage) && '0px'
              }}
            >
              <SearchFormButton
                buttonText={searchBtnText}
                data-testid="HOTELS_SUBMIT_BUTTON"
                handleClick={() => {
                  if (
                    isMobile &&
                    !isHomepage &&
                    MKTG_MWEB_HTL_PROD_HP_APP_MODAL === 'VARIANT' &&
                    window?.PCLN?.pclnEventDispatcher?.dispatch !== undefined
                  ) {
                    window.PCLN.pclnEventDispatcher.dispatch('FIND YOUR HOTEL')
                  }
                }}
                data-rudder-event
                data-sub-type="search"
                data-source={isHomepage ? 'homepage' : 'hotel landing'}
                data-parent-product="STAY"
                data-child-product={watchTripType.split(':').slice(1).join(':')}
                data-cguid={webstats.clientGUID}
                data-email-address={customer.emailAddress}
                {...htlButtonMicroAnimationProps}
              />
              {promoDeal !== PromoDeal.COLLECTIONS &&
                promoDeal !== PromoDeal.EXPRESS_DEAL && (
                  <Box
                    pt={2}
                    mt={1}
                    textAlign={
                      isMobile || isHPWithoutStickyEffect ? 'center' : null
                    }
                  >
                    <Text fontSize={0}>
                      Book a hotel with free cancellation for flexibility
                    </Text>
                  </Box>
                )}
            </Box>
            {showCityMapInMobile &&
              cityMapURL !== 'None' &&
              (!cityMapURL ? (
                <Box data-testid="city-map-shimmer" width={1} mx={2}>
                  <Shimmer
                    height={MOBILE_CITY_MAP_HEIGHT_CONTAINER}
                    width={1}
                    borderRadius="lg"
                  />
                </Box>
              ) : (
                <StyledRelative
                  mb={10}
                  width={1}
                  height={MOBILE_CITY_MAP_HEIGHT_CONTAINER}
                  borderRadius="lg"
                  mx={2}
                >
                  <Image
                    src={cityMapURL}
                    alt={`Map of ${cityName}`}
                    px={0}
                    top="50%"
                    left="50%"
                    width={1}
                    height="auto"
                    position="relative"
                    transform="translate(-50%, -50%)"
                    data-testid="city-map-image"
                  />
                  <StyledAbsolute>
                    <Button
                      size="small"
                      data-testid="open-map-view"
                      onClick={handleSubmit((data: HOTEL_FORM_STATE_TYPE) =>
                        onSubmit({
                          ...data,
                          gaCategory: ANALYTICS_CATEGORY,
                          ...(SEMSearch && {
                            SEMSearch: SEMSearch as SEM_SEARCH_TYPE
                          }),
                          gaAction: isStickyInEffect
                            ? 'Hotel Sticky Search Button'
                            : ANALYTICS_ACTION_HOTELS_SEARCH,
                          mapView: true
                        })
                      )}
                    >
                      <Flex alignItems="center">
                        <Pin size={14} mr={1} />
                        <Text>View Hotels on Map</Text>
                      </Flex>
                    </Button>
                  </StyledAbsolute>
                </StyledRelative>
              ))}
            {!isMobile && !isHPWithoutStickyEffect && showBundleAndSave()}
          </Flex>
        </StickyEffect>
      </>
    </form>
  )
}
